/**
 * Utility functions for handling microphone permissions
 */

export type MicrophonePermissionState =
  | "granted"
  | "denied"
  | "prompt"
  | "unsupported";

export interface MicrophonePermissionResult {
  state: MicrophonePermissionState;
  canRequest: boolean;
  message: string;
}

/**
 * Check the current microphone permission state without triggering a request
 */
export const checkMicrophonePermission =
  async (): Promise<MicrophonePermissionResult> => {
    // Check if MediaDevices API is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return {
        state: "unsupported",
        canRequest: false,
        message: "Microphone access is not supported in this browser.",
      };
    }

    try {
      // Use the Permissions API if available (more accurate)
      if ("permissions" in navigator) {
        const permission = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });

        switch (permission.state) {
          case "granted":
            return {
              state: "granted",
              canRequest: false,
              message: "Microphone access is granted.",
            };
          case "denied":
            return {
              state: "denied",
              canRequest: false,
              message:
                "Microphone access has been denied. Please enable it in your browser settings.",
            };
          case "prompt":
            return {
              state: "prompt",
              canRequest: true,
              message: "Microphone access permission is needed.",
            };
          default:
            return {
              state: "prompt",
              canRequest: true,
              message: "Microphone access permission is needed.",
            };
        }
      }
    } catch (error) {
      const err = error as Error;
      if (
        err.message?.includes("policy violation") ||
        err.name === "NotSupportedError"
      ) {
        return {
          state: "unsupported",
          canRequest: false,
          message:
            "Microphone access is blocked by browser policy. This site must be served over HTTPS to access the microphone.",
        };
      }
      console.warn(
        "Permissions API not fully supported, falling back to getUserMedia check",
        err,
      );
    }

    // Fallback: Try to access devices without actually requesting media
    // This is less reliable but works in browsers without Permissions API
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasLabeledAudioDevices = devices
        .filter((device) => device.kind === "audioinput")
        .some((device) => device.label !== "");

      if (hasLabeledAudioDevices) {
        return {
          state: "granted",
          canRequest: false,
          message: "Microphone access is granted.",
        };
      } else {
        return {
          state: "prompt",
          canRequest: true,
          message: "Microphone access permission is needed.",
        };
      }
    } catch (error) {
      return {
        state: "prompt",
        canRequest: true,
        message: "Unable to check microphone permission status.",
      };
    }
  };

/**
 * Request microphone permission from the user
 */
export const requestMicrophonePermission =
  async (): Promise<MicrophonePermissionResult> => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return {
        state: "unsupported",
        canRequest: false,
        message: "Microphone access is not supported in this browser.",
      };
    }

    try {
      // Request access - this will trigger the browser's permission dialog
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Clean up the stream immediately since we only needed it for permission
      stream.getTracks().forEach((track) => track.stop());

      return {
        state: "granted",
        canRequest: false,
        message: "Microphone access granted successfully.",
      };
    } catch (error) {
      const err = error as DOMException;

      if (err.name === "NotAllowedError") {
        return {
          state: "denied",
          canRequest: false,
          message:
            "Microphone access was denied. Please enable it in your browser settings and refresh the page.",
        };
      } else if (err.name === "NotFoundError") {
        return {
          state: "denied",
          canRequest: false,
          message:
            "No microphone found. Please connect a microphone and try again.",
        };
      } else if (
        err.name === "NotSupportedError" ||
        err.message?.includes("policy violation")
      ) {
        return {
          state: "unsupported",
          canRequest: false,
          message:
            "Microphone access is blocked by browser policy. This site must be served over HTTPS to access the microphone.",
        };
      } else {
        console.error("Microphone permission error:", err);
        return {
          state: "denied",
          canRequest: false,
          message: `Unable to access microphone: ${err.message || "Unknown error"}. Please check your browser settings.`,
        };
      }
    }
  };

/**
 * Check if microphone permission is granted
 */
export const isMicrophonePermissionGranted = async (): Promise<boolean> => {
  const result = await checkMicrophonePermission();
  return result.state === "granted";
};

/**
 * Hook for managing microphone permission state in React components
 */
export const useMicrophonePermission = () => {
  const [permissionState, setPermissionState] =
    React.useState<MicrophonePermissionResult | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const checkPermission = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await checkMicrophonePermission();
      setPermissionState(result);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const requestPermission = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await requestMicrophonePermission();
      setPermissionState(result);
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check permission on mount
  React.useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  return {
    permissionState,
    isLoading,
    checkPermission,
    requestPermission,
    isGranted: permissionState?.state === "granted",
    isDenied: permissionState?.state === "denied",
    canRequest: permissionState?.canRequest ?? false,
  };
};

// Re-export React for the hook
import React from "react";
